import React, { useEffect, useMemo, useState } from 'react';
import { CheckCircle, X, Clock, XCircle, Download, FileText } from 'lucide-react';

export default function EventRegistrations({ eventId, onClose }) {
  const API_BASE = process.env.REACT_APP_BACKEND_URL || '';
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [rows, setRows] = useState([]);
  const [search, setSearch] = useState('');
  const [showManualForm, setShowManualForm] = useState(false);
  const [eventTariffs, setEventTariffs] = useState([]);
  const [manualForm, setManualForm] = useState({
    buyer_name: '',
    buyer_email: '',
    tariffs: [],  // Liste des tarifs sélectionnés: [{tariff_id: '', quantity: 1}]
    participants: [],
    payment_status: 'pending',
    payment_method: ''
  });

  // Fonction d'export CSV
  const exportToCSV = () => {
    const headers = ['ID Réservation', 'Nom', 'Email', 'Billets', 'Montant', 'Statut', 'Méthode', 'Date'];
    const csvRows = [headers.join(',')];

    rows.forEach(r => {
      const billetsStr = r.tariffs_details && r.tariffs_details.length > 0
        ? r.tariffs_details.map(td => `${td.quantity}x ${td.label}`).join(' / ')
        : (r.quantity || 1).toString();
      
      const row = [
        r.id || '',
        `"${r.buyer_name}"`,
        r.buyer_email,
        `"${billetsStr}"`,
        (r.amount_total || 0).toFixed(2),
        r.payment_status || '',
        r.payment_method || '',
        r.created_at ? new Date(r.created_at).toLocaleDateString() : ''
      ];
      csvRows.push(row.join(','));
    });

    const csvContent = csvRows.join('\n');
    const blob = new Blob(['\uFEFF' + csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `inscriptions_event_${eventId}_${new Date().toISOString().split('T')[0]}.csv`;
    link.click();
  };

  // Fonction d'export PDF
  const exportToPDF = () => {
    // Créer un contenu HTML pour impression
    const printContent = `
      <html>
        <head>
          <title>Inscriptions Événement</title>
          <style>
            body { font-family: Arial, sans-serif; padding: 20px; }
            h1 { color: rgb(196, 150, 96); }
            table { width: 100%; border-collapse: collapse; margin-top: 20px; }
            th, td { border: 1px solid #ddd; padding: 8px; text-align: left; font-size: 12px; }
            th { background-color: rgb(196, 150, 96); color: white; }
            tr:nth-child(even) { background-color: #f9f9f9; }
            .status-paid { color: green; font-weight: bold; }
            .status-pending { color: orange; font-weight: bold; }
            .status-canceled { color: red; font-weight: bold; }
          </style>
        </head>
        <body>
          <h1>Liste des Inscriptions</h1>
          <p><strong>Date d'export:</strong> ${new Date().toLocaleDateString()} ${new Date().toLocaleTimeString()}</p>
          <p><strong>Total inscriptions:</strong> ${rows.length}</p>
          <table>
            <thead>
              <tr>
                <th>ID Réservation</th>
                <th>Nom</th>
                <th>Email</th>
                <th>Billets</th>
                <th>Montant</th>
                <th>Statut</th>
                <th>Méthode</th>
              </tr>
            </thead>
            <tbody>
              ${rows.map(r => {
                const billetsStr = r.tariffs_details && r.tariffs_details.length > 0
                  ? r.tariffs_details.map(td => `${td.quantity}x ${td.label}`).join('<br/>')
                  : (r.quantity || 1);
                
                const statusClass = r.payment_status === 'paid' ? 'status-paid' : 
                                   r.payment_status === 'pending' ? 'status-pending' : 'status-canceled';
                
                return `
                  <tr>
                    <td style="font-size: 10px; word-break: break-all;">${r.id || 'N/A'}</td>
                    <td>${r.buyer_name}</td>
                    <td>${r.buyer_email}</td>
                    <td>${billetsStr}</td>
                    <td>€${(r.amount_total || 0).toFixed(2)}</td>
                    <td class="${statusClass}">${r.payment_status || ''}</td>
                    <td>${r.payment_method || ''}</td>
                  </tr>
                `;
              }).join('')}
            </tbody>
          </table>
        </body>
      </html>
    `;

    const printWindow = window.open('', '', 'width=800,height=600');
    printWindow.document.write(printContent);
    printWindow.document.close();
    printWindow.focus();
    setTimeout(() => {
      printWindow.print();
      printWindow.close();
    }, 250);
  };

  const loadRegs = async () => {
    setLoading(true); setError(null);
    try {
      const url = API_BASE ? new URL(`/api/admin/events/${eventId}/registrations`, API_BASE).toString() : `/api/admin/events/${eventId}/registrations`;
      const headers = {};
      const token = localStorage.getItem('authToken');
      if (token) headers['Authorization'] = `Bearer ${token}`;
      const res = await fetch(url, { headers });
      if (!res.ok) throw new Error('Erreur chargement inscriptions');
      const data = await res.json();
      setRows(data.registrations || []);
    } catch (e) { setError(e.message); setRows([]); }
    finally { setLoading(false); }
  };

  const loadEventTariffs = async () => {
    try {
      const url = API_BASE ? new URL(`/api/admin/events`, API_BASE).toString() : `/api/admin/events`;
      const headers = {};
      const token = localStorage.getItem('authToken');
      if (token) headers['Authorization'] = `Bearer ${token}`;
      const res = await fetch(url, { headers });
      if (!res.ok) throw new Error('Erreur chargement événement');
      const data = await res.json();
      const event = data.events?.find(e => e.id === eventId);
      if (event) {
        setEventTariffs(event.tariffs || []);
      }
    } catch (e) {
      console.error('Erreur chargement tarifs:', e);
    }
  };

  useEffect(() => { 
    loadRegs();
    loadEventTariffs();
    // eslint-disable-next-line
  }, [eventId]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    let list = rows;
    if (q) {
      list = rows.filter(r => (
        (r.buyer_name || '').toLowerCase().includes(q) ||
        (r.buyer_email || '').toLowerCase().includes(q) ||
        (r.payment_status || '').toLowerCase().includes(q) ||
        (r.payment_method || '').toLowerCase().includes(q)
      ));
    }
    return list;
  }, [rows, search]);

  const createManualRegistration = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const url = API_BASE ? new URL(`/api/admin/events/${eventId}/registrations/manual`, API_BASE).toString() : `/api/admin/events/${eventId}/registrations/manual`;
      const headers = { 'Content-Type': 'application/json' };
      const token = localStorage.getItem('authToken');
      if (token) headers['Authorization'] = `Bearer ${token}`;
      
      const payload = {
        ...manualForm,
        participants: manualForm.participants.filter(p => p.trim() !== '')
      };
      
      const res = await fetch(url, {
        method: 'POST',
        headers,
        body: JSON.stringify(payload)
      });
      
      if (!res.ok) {
        const errorData = await res.json().catch(() => ({}));
        throw new Error(errorData.detail || 'Erreur création inscription');
      }
      
      alert('Inscription créée avec succès !');
      setShowManualForm(false);
      setManualForm({
        buyer_name: '',
        buyer_email: '',
        tariffs: [],
        participants: [],
        payment_status: 'pending',
        payment_method: ''
      });
      await loadRegs();
    } catch (e) {
      alert(e.message);
    } finally {
      setLoading(false);
    }
  };

  const updateStatus = async (registrationId, payment_status, payment_method=null) => {
    try {
      const headers = { 'Content-Type': 'application/json' };
      const token = localStorage.getItem('authToken');
      if (token) headers['Authorization'] = `Bearer ${token}`;
      const url = new URL(`/api/admin/events/registrations/${registrationId}`, API_BASE);
      
      // Pour pending, on efface explicitement payment_method
      const payload = { 
        payment_status, 
        payment_method: payment_status === 'pending' ? null : payment_method 
      };
      
      const res = await fetch(url.toString(), { method: 'PUT', headers, body: JSON.stringify(payload) });
      if (!res.ok) throw new Error('Erreur mise à jour');
      
      // Recharger les inscriptions pour afficher les changements
      await loadRegs();
    } catch (e) { 
      alert(e.message); 
    }
  };

  return (
    <div className="bg-white rounded-lg shadow p-6 mb-6">
      <div className="flex justify-between items-center mb-4">
        <h2 className="text-xl font-semibold">Inscriptions</h2>
        <button onClick={onClose} className="text-gray-500 hover:text-gray-700">✕</button>
      </div>

      <div className="flex items-center justify-between mb-3">
        <div className="text-sm" style={{ fontFamily: '"Lora", serif' }}>
          <label className="mr-2">Search:</label>
          <input value={search} onChange={(e)=>setSearch(e.target.value)} className="border rounded px-2 py-1" placeholder="Rechercher..." />
        </div>
        <div className="flex items-center gap-3">
          <button 
            onClick={exportToCSV}
            className="text-white px-4 py-2 rounded text-sm flex items-center gap-2"
            style={{ backgroundColor: 'rgb(34, 197, 94)' }}
            title="Exporter en CSV"
          >
            <Download className="w-4 h-4" />
            CSV
          </button>
          <button 
            onClick={exportToPDF}
            className="text-white px-4 py-2 rounded text-sm flex items-center gap-2"
            style={{ backgroundColor: 'rgb(239, 68, 68)' }}
            title="Exporter en PDF"
          >
            <FileText className="w-4 h-4" />
            PDF
          </button>
          <button 
            onClick={() => setShowManualForm(!showManualForm)} 
            className="text-white px-4 py-2 rounded text-sm"
            style={{ backgroundColor: 'rgb(196, 150, 96)' }}
            onMouseEnter={(e) => e.target.style.backgroundColor = 'rgb(176, 130, 76)'}
            onMouseLeave={(e) => e.target.style.backgroundColor = 'rgb(196, 150, 96)'}
          >
            {showManualForm ? 'Annuler' : '+ Inscription manuelle'}
          </button>
          <div className="text-sm text-gray-600">{filtered.length} inscription(s)</div>
        </div>
      </div>

      {/* Formulaire d'inscription manuelle */}
      {showManualForm && (
        <div className="bg-gray-50 border rounded-lg p-4 mb-4">
          <h3 className="text-lg font-semibold mb-3">Nouvelle inscription manuelle</h3>
          <form onSubmit={createManualRegistration} className="space-y-3">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div>
                <label className="block text-sm font-medium mb-1">Nom de l'acheteur *</label>
                <input 
                  type="text" 
                  required 
                  value={manualForm.buyer_name}
                  onChange={(e) => setManualForm({...manualForm, buyer_name: e.target.value})}
                  className="w-full border rounded px-3 py-2"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Email *</label>
                <input 
                  type="email" 
                  required 
                  value={manualForm.buyer_email}
                  onChange={(e) => setManualForm({...manualForm, buyer_email: e.target.value})}
                  className="w-full border rounded px-3 py-2"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium mb-2">Tarifs et quantités *</label>
              <div className="space-y-2">
                {eventTariffs.map((tariff) => {
                  const currentTariff = manualForm.tariffs.find(t => t.tariff_id === tariff.id);
                  const quantity = currentTariff ? currentTariff.quantity : 0;
                  
                  return (
                    <div key={tariff.id} className="flex items-center gap-3 p-2 border rounded bg-gray-50">
                      <div className="flex-1">
                        <span className="font-medium">{tariff.label}</span>
                        <span className="text-gray-600 ml-2">({tariff.price}€)</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <label className="text-sm text-gray-600">Quantité:</label>
                        <input 
                          type="number" 
                          min="0" 
                          value={quantity}
                          onChange={(e) => {
                            const qty = parseInt(e.target.value) || 0;
                            let newTariffs = [...manualForm.tariffs];
                            
                            if (qty === 0) {
                              // Retirer ce tarif
                              newTariffs = newTariffs.filter(t => t.tariff_id !== tariff.id);
                            } else {
                              const existingIndex = newTariffs.findIndex(t => t.tariff_id === tariff.id);
                              if (existingIndex >= 0) {
                                newTariffs[existingIndex].quantity = qty;
                              } else {
                                newTariffs.push({ tariff_id: tariff.id, quantity: qty });
                              }
                            }
                            
                            // Calculer le nombre total de participants
                            const totalQty = newTariffs.reduce((sum, t) => sum + t.quantity, 0);
                            const newParticipants = Array(totalQty).fill('').map((_, i) => manualForm.participants[i] || '');
                            
                            setManualForm({...manualForm, tariffs: newTariffs, participants: newParticipants});
                          }}
                          className="w-20 border rounded px-2 py-1 text-center"
                        />
                      </div>
                    </div>
                  );
                })}
              </div>
              {manualForm.tariffs.length > 0 && (
                <div className="mt-2 text-sm text-gray-600">
                  Total: {manualForm.tariffs.reduce((sum, t) => {
                    const tariff = eventTariffs.find(et => et.id === t.tariff_id);
                    return sum + (tariff ? tariff.price * t.quantity : 0);
                  }, 0).toFixed(2)}€ 
                  ({manualForm.tariffs.reduce((sum, t) => sum + t.quantity, 0)} place(s))
                </div>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium mb-1">Noms des participants ({manualForm.participants.length})</label>
              {manualForm.participants.map((p, i) => (
                <input 
                  key={i}
                  type="text" 
                  value={p}
                  onChange={(e) => {
                    const newParticipants = [...manualForm.participants];
                    newParticipants[i] = e.target.value;
                    setManualForm({...manualForm, participants: newParticipants});
                  }}
                  placeholder={`Participant ${i+1}`}
                  className="w-full border rounded px-3 py-2 mb-2"
                />
              ))}
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div>
                <label className="block text-sm font-medium mb-1">Statut de paiement *</label>
                <select 
                  required
                  value={manualForm.payment_status}
                  onChange={(e) => setManualForm({...manualForm, payment_status: e.target.value})}
                  className="w-full border rounded px-3 py-2"
                >
                  <option value="pending">En attente</option>
                  <option value="paid">Payé</option>
                  <option value="cancelled">Annulé</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Méthode de paiement</label>
                <select 
                  value={manualForm.payment_method}
                  onChange={(e) => setManualForm({...manualForm, payment_method: e.target.value})}
                  className="w-full border rounded px-3 py-2"
                >
                  <option value="">-- Sélectionner --</option>
                  <option value="liquide">Liquide</option>
                  <option value="virement">Virement bancaire</option>
                  <option value="carte">Carte bancaire</option>
                  <option value="cheque">Chèque</option>
                  <option value="sur_place">Sur place</option>
                </select>
              </div>
            </div>

            <div className="flex gap-2 pt-2">
              <button 
                type="submit" 
                disabled={loading}
                className="text-white px-4 py-2 rounded"
                style={{ backgroundColor: 'rgb(196, 150, 96)' }}
                onMouseEnter={(e) => e.target.style.backgroundColor = 'rgb(176, 130, 76)'}
                onMouseLeave={(e) => e.target.style.backgroundColor = 'rgb(196, 150, 96)'}
              >
                Créer l'inscription
              </button>
              <button 
                type="button" 
                onClick={() => setShowManualForm(false)}
                className="bg-gray-500 text-white px-4 py-2 rounded hover:bg-gray-600"
              >
                Annuler
              </button>
            </div>
          </form>
        </div>
      )}

      <div className="overflow-x-auto" style={{ maxWidth: '100%' }}>
        <table className="min-w-full border-t" style={{ tableLayout: 'auto', whiteSpace: 'nowrap' }}>
          <thead className="bg-gray-50">
            <tr>
              <th className="text-left text-xs font-semibold text-gray-700 px-3 py-2 border-b">ID Réservation</th>
              <th className="text-left text-xs font-semibold text-gray-700 px-3 py-2 border-b">Acheteur</th>
              <th className="text-left text-xs font-semibold text-gray-700 px-3 py-2 border-b">Email</th>
              <th className="text-left text-xs font-semibold text-gray-700 px-3 py-2 border-b">Billets</th>
              <th className="text-left text-xs font-semibold text-gray-700 px-3 py-2 border-b">Participants</th>
              <th className="text-left text-xs font-semibold text-gray-700 px-3 py-2 border-b">Montant</th>
              <th className="text-left text-xs font-semibold text-gray-700 px-3 py-2 border-b">Statut</th>
              <th className="text-left text-xs font-semibold text-gray-700 px-3 py-2 border-b">Méthode</th>
              <th className="text-left text-xs font-semibold text-gray-700 px-3 py-2 border-b">Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading && <tr><td colSpan={8} className="px-4 py-6 text-center text-gray-500">Chargement...</td></tr>}
            {!loading && error && <tr><td colSpan={8} className="px-4 py-6 text-center text-red-600">{error}</td></tr>}
            {!loading && !error && filtered.length === 0 && <tr><td colSpan={8} className="px-4 py-6 text-center text-gray-500">Aucune inscription</td></tr>}
            {!loading && !error && filtered.map((r) => {
              // Traduire le statut
              const statusLabel = r.payment_status === 'pending' ? 'En attente' : r.payment_status;
              const statusColor = r.payment_status === 'paid' ? 'bg-green-100 text-green-800' : 
                                  r.payment_status === 'pending' ? 'bg-yellow-100 text-yellow-800' :
                                  r.payment_status === 'cancelled' ? 'bg-red-100 text-red-800' : 'bg-gray-100 text-gray-800';
              
              return (
                <tr key={r.id} className="odd:bg-white even:bg-gray-50 hover:bg-blue-50">
                  <td className="px-3 py-2 text-xs text-gray-600 border-b font-mono" title={r.id}>
                    {r.id ? r.id.substring(0, 8) + '...' : 'N/A'}
                  </td>
                  <td className="px-3 py-2 text-sm text-gray-900 border-b font-medium">{r.buyer_name}</td>
                  <td className="px-3 py-2 text-xs text-gray-600 border-b">{r.buyer_email}</td>
                  <td className="px-3 py-2 text-xs text-gray-700 border-b">
                    {r.tariffs_details && r.tariffs_details.length > 0 ? (
                      <div className="space-y-1">
                        {r.tariffs_details.map((td, idx) => (
                          <div key={idx} className="text-xs">
                            {td.quantity}x {td.label} ({td.price.toFixed(2)}€)
                          </div>
                        ))}
                      </div>
                    ) : (
                      <span className="text-center">{r.quantity || 1}</span>
                    )}
                  </td>
                  <td className="px-3 py-2 text-xs text-gray-600 border-b">
                    {(r.participants || []).length > 0 ? (
                      <div className="space-y-1">
                        {(r.participants || []).map((p, idx) => {
                          const name = typeof p === 'string' ? p : p.name;
                          return name ? (
                            <div key={idx} className="py-0.5">
                              {name}
                            </div>
                          ) : null;
                        })}
                      </div>
                    ) : (
                      <span className="text-gray-400">-</span>
                    )}
                  </td>
                  <td className="px-3 py-2 text-xs text-gray-700 border-b font-semibold">€{(r.amount_total || 0).toFixed(2)}</td>
                  <td className="px-3 py-2 border-b">
                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${statusColor}`}>
                      {statusLabel}
                    </span>
                  </td>
                  <td className="px-3 py-2 text-xs text-gray-600 border-b">
                    {r.payment_method === 'stripe' ? 'Stripe' : r.payment_method || '—'}
                  </td>
                  <td className="px-3 py-2 border-b">
                    <div className="flex items-center gap-1">
                      <button 
                        onClick={() => updateStatus(r.id, 'paid', 'virement')} 
                        className="px-2 py-1 rounded bg-green-600 text-white text-xs font-medium flex items-center gap-1 transition-all hover:bg-green-700"
                        title="Payé par virement"
                      >
                        <CheckCircle className="w-3 h-3" />
                        <span>Virement</span>
                      </button>
                      <button 
                        onClick={() => updateStatus(r.id, 'paid', 'sur_place')} 
                        className="px-2 py-1 rounded bg-green-600 text-white text-xs font-medium flex items-center gap-1 transition-all hover:bg-green-700"
                        title="Payé sur place"
                      >
                        <CheckCircle className="w-3 h-3" />
                        <span>Sur place</span>
                      </button>
                      <button 
                        onClick={() => updateStatus(r.id, 'pending', null)} 
                        className="px-2 py-1 rounded bg-orange-500 text-white text-xs font-medium flex items-center gap-1 transition-all hover:bg-orange-600"
                        title="En attente"
                      >
                        <Clock className="w-3 h-3" />
                      </button>
                      <button 
                        onClick={() => updateStatus(r.id, 'cancelled', null)} 
                        className="px-2 py-1 rounded bg-red-600 text-white text-xs font-medium flex items-center gap-1 transition-all hover:bg-red-700"
                        title="Annuler"
                      >
                        <XCircle className="w-3 h-3" />
                      </button>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}